/**
 * Shared helper for tasks + communications saved-view derivation.
 *
 * Each field declaration optionally carries `savedViewMembership: readonly string[]`
 * listing the saved-view ids it appears in by default. This helper inverts that
 * mapping: given a view id, return every field id that opts into the view,
 * preserving field-map declaration order.
 *
 * Using this helper means saved-view `visibleColumns` are derived from field
 * declarations — so adding/removing a column from a view touches a single
 * field file instead of multiple saved-view files.
 *
 * Lives under `features/tasks/` because both tasks and communications share
 * the same parent feature directory.
 */
import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";

export function collectFieldsForSavedView<TFieldId extends string>(
  viewId: string,
  fields: Readonly<Record<TFieldId, SimpleFieldConfig>>
): TFieldId[] {
  const result: TFieldId[] = [];
  for (const [fieldId, fieldConfig] of Object.entries(fields) as Array<
    [TFieldId, SimpleFieldConfig]
  >) {
    const membership = fieldConfig.savedViewMembership;
    if (membership && membership.includes(viewId)) {
      result.push(fieldId);
    }
  }
  return result;
}
